// ATLAS option tree lookups + the auto-fill chain. Selecting at any level
// fills every level below with the first (most common) option; the user then
// revises any dropdown. Custom entries live alongside (not in the tree).
import type { Sector } from '../../domain/olmps';
import type { OracleAim } from '../oracle-spine-data';
import { TELOS_CASCADE_PRESETS } from '../telos-cascade';
import { SECTOR_DEITY_MAP } from '../question-banks';
import { SHAPE_BY_ID, SHAPE_DEITIES } from '../../v10/shapeRoster';
import type { TreeAim, TreeEndeavour, TreeGoal, TreeToday } from './types';
import { SOCIAL_TREE } from './social';
import { HEALTH_TREE } from './health';
import { ADMIN_TREE } from './admin';
import { PERSONAL_TREE } from './personal';
import { ECONOMIC_TREE } from './economic';

export type { TreeAim, TreeEndeavour, TreeGoal, TreeToday };

export const ATLAS_TREE: Record<Sector, TreeEndeavour[]> = {
  S: SOCIAL_TREE,
  H: HEALTH_TREE,
  A: ADMIN_TREE,
  P: PERSONAL_TREE,
  E: ECONOMIC_TREE,
};

const DOMAIN: Record<Sector, string> = { S: 'telos-social', H: 'telos-health', A: 'telos-admin', P: 'telos-personal', E: 'telos-economic' };

/** The sector's general principles (TELOS domain principles). */
export const sectorPrinciples = (s: Sector): string[] =>
  TELOS_CASCADE_PRESETS.find((p) => p.domainId === DOMAIN[s])?.principles.map((p) => p.text) ?? [];

const slug = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);
/** Stable Aim id for a tree aim (KYRS aim_ref = `${sector}:${id}`). */
export const treeAimId = (a: Pick<TreeAim, 't'>) => `atlas-${slug(a.t)}`;
export const toOracleAim = (a: TreeAim): OracleAim => ({ id: treeAimId(a), label: a.t, cadence: a.c });

export const endeavoursFor = (s: Sector) => ATLAS_TREE[s].map((x) => x.e);

/** The sector deity an Endeavour trains — set per Endeavour in the tree
 *  (the 25-deity SHAPE roster, v10/shapeRoster.ts). A custom Endeavour gets the deity whose
 *  themes it matches best (ATLAS then asks the AI for the closest one), else the sector's first. */
export function deityForEndeavour(s: Sector, e: string | null | undefined): string | null {
  return findEndeavour(s, e)?.deity ?? (e ? matchDeity(s, e) : null) ?? SHAPE_DEITIES.find((d) => d.sector === s)?.id ?? null;
}

/** Closest SHAPE deity in a sector for free text, by its theme and role words; null when none match. */
export function matchDeity(s: Sector, text: string): string | null {
  const hay = ` ${text.toLowerCase()} `;
  let best: string | null = null;
  let top = 0;
  for (const d of SHAPE_DEITIES.filter((x) => x.sector === s)) {
    const words = [...d.themes.map((w) => w.toLowerCase()), ...d.role.toLowerCase().split(/[^a-z]+/).filter((w) => w.length > 3)];
    const score = words.reduce((n, w) => n + (hay.includes(w) ? w.length : 0), 0);
    if (score > top) {
      top = score;
      best = d.id;
    }
  }
  return best;
}
/** The deity a sector is training now: the saved Goal when it is on the current roster, otherwise
 *  (older saves hold retired ids like 'psyche' or 'eunomia') the deity its Endeavour is tagged with. */
export function currentDeity(s: Sector, goal: string | null | undefined, e: string | null | undefined): string | null {
  return goal && SHAPE_BY_ID[goal] ? goal : deityForEndeavour(s, e);
}
export const deityNameOf = (s: Sector, id: string | null | undefined) =>
  id ? SHAPE_BY_ID[id]?.name ?? SECTOR_DEITY_MAP[s]?.find((d) => d.id === id)?.name ?? id.toUpperCase() : null;
export const findEndeavour = (s: Sector, e: string | null | undefined) => (e ? ATLAS_TREE[s].find((x) => x.e === e) : undefined);
export const goalsFor = (s: Sector, e: string | null | undefined): TreeGoal[] => findEndeavour(s, e)?.g ?? [];
export const findGoal = (s: Sector, e: string | null | undefined, g: string | null | undefined) =>
  g ? goalsFor(s, e).find((x) => x.t === g) : undefined;

/** The TELOS habits ("I …", one principle each — the web TELOS formula) as Aim
 *  options; their actions become the day options. */
export function telosHabitAims(s: Sector): TreeAim[] {
  const preset = TELOS_CASCADE_PRESETS.find((p) => p.domainId === DOMAIN[s]);
  return (preset?.habits ?? []).map((h) => ({
    t: h.name,
    c: 'weekly',
    p: h.principle,
    d: h.tasks.map((task, i) => ({ t: task.text, s: [task.text, ...h.tasks.filter((_, j) => j !== i).map((x) => x.text)] })),
  }));
}

/** Aim options under a Goal: the goal's aims, then the sector's TELOS habits. */
export function aimsFor(s: Sector, goal: TreeGoal | undefined): TreeAim[] {
  const out = [...(goal?.a ?? [])];
  for (const h of telosHabitAims(s)) if (!out.some((a) => a.t === h.t)) out.push(h);
  return out;
}

/** Every tree aim in the sector, for matching a saved aim back to its principle / days. */
function allAims(s: Sector): TreeAim[] {
  return [...ATLAS_TREE[s].flatMap((e) => e.g.flatMap((g) => g.a)), ...telosHabitAims(s)];
}
export const findAim = (s: Sector, label: string) => allAims(s).find((a) => a.t === label);

/** Today options for the selected aims (union, first aim first). */
export function todaysFor(s: Sector, aimLabels: string[]): TreeToday[] {
  const out: TreeToday[] = [];
  for (const l of aimLabels) for (const d of findAim(s, l)?.d ?? []) if (!out.some((x) => x.t === d.t)) out.push(d);
  return out;
}
export function stepsFor(s: Sector, aimLabels: string[], today: string | null | undefined): string[] {
  return today ? todaysFor(s, aimLabels).find((d) => d.t === today)?.s ?? [] : [];
}
/** Principle options: the selected aims' principles, then the sector's general ones. */
export function principlesFor(s: Sector, aimLabels: string[]): string[] {
  const out: string[] = [];
  for (const l of aimLabels) {
    const p = findAim(s, l)?.p;
    if (p && !out.includes(p)) out.push(p);
  }
  for (const p of sectorPrinciples(s)) if (!out.includes(p)) out.push(p);
  return out;
}

export interface Fill {
  goal?: string;
  aims?: OracleAim[];
  today?: string;
  step?: string;
  principles?: string[];
}

/** Defaults below a chosen Goal: first aim → its first day → that day's first step.
 *  A custom Goal (not in the tree) clears the levels below; AI options refill them. */
export function fillFromGoal(goal: TreeGoal | undefined): Fill {
  const aim = goal?.a[0];
  if (!aim) return { aims: [], today: '', step: '' };
  return { aims: [toOracleAim(aim)], ...fillFromAims([aim]) };
}
export function fillFromAims(aims: TreeAim[]): Fill {
  const day = aims[0]?.d[0];
  return {
    today: day?.t ?? '',
    step: day?.s[0] ?? '',
    principles: aims.map((a) => a.p).filter((p, i, arr) => p && arr.indexOf(p) === i),
  };
}
/** Defaults below a chosen Endeavour: its first Goal and everything under it.
 *  A custom Endeavour clears the levels below, so no stale Goal from the old one survives. */
export function fillFromEndeavour(s: Sector, e: string): Fill {
  const goal = findEndeavour(s, e)?.g[0];
  return goal ? { goal: goal.t, ...fillFromGoal(goal) } : { goal: '', aims: [], today: '', step: '' };
}

// ── Orb ORIENT pre-fill ─────────────────────────────────────────────────────
// "What am I doing, and why?" = the item at the orb's level + ", so " + the
// item above it; "How will I know I'm done?" = that item's completion line.
// Both come pre-written from the tree; custom items get a composed sentence
// (and no done-line, so the field shows its question).

export interface OrientPrefill {
  doing: string;
  done: string;
}
export interface OrientPath {
  endeavour: string | null;
  goal: string | null;
  aim: string | null;
  today: string | null;
  step: string | null;
}

const lowerFirst = (x: string) => (x ? x.charAt(0).toLowerCase() + x.slice(1) : x);
const strip = (x: string) => x.replace(/[.\s]+$/, '');
const compose = (a: string | null, b: string | null) =>
  a ? (b ? `${strip(a)}, so ${lowerFirst(strip(b))}.` : `${strip(a)}.`) : '';

export function orientPrefill(s: Sector, level: 'E' | 'G' | 'A' | 'T' | 'S', path: OrientPath): OrientPrefill {
  const E = findEndeavour(s, path.endeavour);
  const G = path.goal ? E?.g.find((g) => g.t === path.goal) : undefined;
  const A = path.aim ? G?.a.find((a) => a.t === path.aim) ?? findAim(s, path.aim) : undefined;
  const T = path.today ? A?.d.find((d) => d.t === path.today) : undefined;
  const si = T && path.step ? T.s.indexOf(path.step) : -1;
  switch (level) {
    case 'E':
      return { doing: E?.w ?? compose(path.endeavour, null), done: E?.k ?? '' };
    case 'G':
      return { doing: G?.w ?? compose(path.goal, path.endeavour), done: G?.k ?? '' };
    case 'A':
      return { doing: A?.w ?? compose(path.aim, path.goal), done: A?.k ?? '' };
    case 'T':
      return { doing: T?.w ?? compose(path.today, path.aim), done: T?.k ?? '' };
    case 'S':
      return {
        doing: (si >= 0 ? T?.sw?.[si] : undefined) ?? compose(path.step, path.today),
        done: (si >= 0 ? T?.sk?.[si] : undefined) ?? '',
      };
  }
}

/** Fill any missing level of a path with the tree's default (first) option, so
 *  the orb always has a sentence once an Endeavour exists. */
export function resolvePath(s: Sector, p: OrientPath): OrientPath {
  const E = findEndeavour(s, p.endeavour);
  const goal = p.goal ?? E?.g[0]?.t ?? null;
  const G = goal ? E?.g.find((g) => g.t === goal) : undefined;
  const aim = p.aim ?? G?.a[0]?.t ?? null;
  const A = aim ? G?.a.find((a) => a.t === aim) ?? findAim(s, aim) : undefined;
  const today = p.today ?? A?.d[0]?.t ?? null;
  const T = today ? A?.d.find((d) => d.t === today) : undefined;
  const step = p.step ?? T?.s[0] ?? null;
  return { endeavour: p.endeavour, goal, aim, today, step };
}
